import { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  App,
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Select,
  Table,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { schoolDisplayName } from "../../utils/schoolDisplay.js";
import {
  examDateRows,
  examSchoolOptions,
  prepareExamSave,
} from "../../lib/examSchedules.js";
import {
  changeExamRange,
  examPlan,
  extendExamForm,
  gradeOptions,
  removeExamEdge,
} from "./examForm.js";
import "./examForm.css";

// The parent remounts this editor for each explicit open, never for a save failure.
export function ExamScheduleDrawer({ initial, folderId, store, onClose }) {
  const [form, setForm] = useState(() => structuredClone(initial));
  const [error, setError] = useState("");
  const [pending, setPending] = useState(null);
  const [checking, setChecking] = useState(false);
  const [changingRange, setChangingRange] = useState(false);
  const [rangeKey, setRangeKey] = useState(0);
  const submitting = useRef(false);
  const [antForm] = Form.useForm();
  const { modal, message } = App.useApp();
  useEffect(() => {
    store.setExamEditorGuard(true);
    return () => store.setExamEditorGuard(false);
  }, [store.setExamEditorGuard]);
  useEffect(() => {
    const preserveDraft = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    globalThis.addEventListener("beforeunload", preserveDraft);
    return () => globalThis.removeEventListener("beforeunload", preserveDraft);
  }, []);
  const locked = store.saving || checking || !!pending || changingRange;
  const schools = useMemo(() => {
    const result = examSchoolOptions(store.state.academyInfo?.schools || []);
    if (
      initial.schoolChoiceId && initial.schoolInfo &&
      !result.some((s) => s.value === initial.schoolChoiceId)
    ) {
      result.push({
        value: initial.schoolChoiceId,
        name: initial.school,
        schoolLevel: initial.schoolInfo.schoolLevel,
        label: schoolDisplayName(initial.school, store.state.academyInfo),
        detail: `${schoolDisplayName(initial.school, store.state.academyInfo)} · 기존 비활성 학교 (유지)`,
      });
    }
    return result;
  }, [store.state.academyInfo?.schools, initial]);
  const selectedSchool = schools.find((s) => s.value === form.schoolChoiceId);
  const dates = examDateRows(form.inputStart, form.inputEnd);
  function changeRange(values) {
    if (locked || !values?.[0] || !values?.[1]) return;
    const change = changeExamRange(
      form,
      values[0].format("YYYY-MM-DD"),
      values[1].format("YYYY-MM-DD"),
    );
    if (!change.removedInputs.length) {
      setForm(change.form);
      return;
    }
    setChangingRange(true);
    modal.confirm({
      title: "기간 밖의 입력을 제거할까요?",
      content: `시험·직보가 입력된 날짜가 표에서 제외됩니다: ${
        change.removedInputs.join(", ")
      }. 저장 전까지 서버 기록은 바뀌지 않습니다.`,
      okText: "기간 변경",
      cancelText: "취소",
      okButtonProps: { danger: true },
      onOk: () => {
        setForm(change.form);
        setChangingRange(false);
      },
      onCancel: () => {
        setRangeKey((key) => key + 1);
        setChangingRange(false);
      },
    });
  }
  function close() {
    if (!locked && !submitting.current) onClose();
  }
  async function submit(request) {
    if (submitting.current) return;
    submitting.current = true;
    setError("");
    try {
      const result = await store.saveExamPeriod(request);
      if (result?.status === "unknown") {
        setPending(request);
        return;
      }
      if (!result) {
        setPending(null);
        setError(
          "저장하지 못했습니다. 입력 내용은 유지됩니다. 다른 직원이 수정했다면 현재 내용을 확인한 뒤 다시 열어 주세요.",
        );
        return;
      }
      message[result.deleted ? "warning" : "success"](
        {
          content: result.deleted
            ? "저장은 확인됐지만 이후 삭제된 일정입니다. 다시 등록하지 않습니다."
            : "일정을 저장했습니다.",
          className: "exam-schedule-message",
        },
      );
      onClose();
    } finally {
      submitting.current = false;
    }
  }
  async function save() {
    if (locked || submitting.current) return;
    try {
      await submit(prepareExamSave(folderId, form));
    } catch (failure) {
      setError(failure.message);
    }
  }
  async function confirmPending() {
    if (!pending || submitting.current || store.saving || checking) return;
    setChecking(true);
    try {
      const result = await store.getExamSaveResult(pending.requestId);
      if (result.status === "saved" && result.deleted) {
        message.warning({
          content: "저장은 확인됐지만 이후 삭제된 일정입니다.",
          className: "exam-schedule-message",
        });
        onClose();
        return;
      }
      // Do not create a new request ID after an ambiguous HTTP failure.
      await submit(pending);
    } catch {
      setError(
        "저장 결과를 아직 확인할 수 없습니다. 입력과 원래 요청을 유지합니다. 연결을 확인한 뒤 다시 확인해 주세요.",
      );
    } finally {
      setChecking(false);
    }
  }
  function setPlan(date, field, value) {
    setForm((current) => ({
      ...current,
      subjects: {
        ...current.subjects,
        [date]: { ...examPlan(current, date), [field]: value },
      },
    }));
  }
  function removeDate(date) {
    const plan = examPlan(form, date);
    const remove = () => setForm((current) => removeExamEdge(current, date));
    if (plan.subject.trim() || plan.direct.trim()) {
      modal.confirm({
        title: "입력한 날짜 행을 삭제할까요?",
        content:
          "이 날짜의 시험·직보 입력이 표에서 제거됩니다. 저장 전까지 서버 기록은 바뀌지 않습니다.",
        okText: "삭제",
        cancelText: "취소",
        okButtonProps: { danger: true },
        onOk: remove,
      });
    } else remove();
  }
  return (
    <Drawer
      className="exam-period-drawer"
      title={initial.id ? "시험 일정 수정" : "시험 일정 추가"}
      open
      onClose={close}
      maskClosable={!locked}
      keyboard={!locked}
      closable={!locked}
      width={760}
      footer={
        <div className="overlay-footer-actions">
          <Button disabled={locked} onClick={close}>취소</Button>
          <Button
            type="primary"
            loading={store.saving}
            disabled={!!pending || checking || changingRange}
            onClick={() => antForm.submit()}
          >
            저장
          </Button>
        </div>
      }
    >
      <Form
        form={antForm}
        layout="vertical"
        initialValues={{
          schoolChoiceId: initial.schoolChoiceId || undefined,
          grade: initial.grade || undefined,
        }}
        onFinish={save}
        className="exam-period-form"
      >
        {error && <Alert type="error" showIcon message={error} role="alert" />}
        {pending && (
          <Alert
            type="warning"
            showIcon
            message="저장 결과를 확인하고 있습니다. 중복 등록을 막기 위해 입력을 잠시 잠급니다."
            action={
              <Button
                loading={checking || store.saving}
                onClick={confirmPending}
              >
                같은 요청으로 결과 확인
              </Button>
            }
          />
        )}
        {initial.contractVersion === 1 && (
          <Alert
            type="info"
            showIcon
            message={`기존 일정: ${schoolDisplayName(initial.school, store.state.academyInfo)}. 학교와 학년을 직접 선택하면 연결됩니다. 기존 이름은 이력으로 보존합니다.`}
          />
        )}
        <section className="exam-period-form-section">
          <h3>학교 및 학년</h3>
          <div className="exam-period-form-grid">
            <Form.Item
              name="schoolChoiceId"
              label="학교"
              rules={[{
                required: true,
                message: "등록된 학교를 선택해 주세요.",
              }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                disabled={locked}
                options={schools}
                optionRender={(option) => (
                  <Tooltip
                    title={option.data.detail}
                    trigger={["hover", "focus"]}
                  >
                    <span tabIndex={0} aria-label={option.data.detail}>
                      {option.data.label}
                    </span>
                  </Tooltip>
                )}
                placeholder="등록된 학교 검색·선택"
                onChange={(id) => {
                  const school = schools.find((s) => s.value === id);
                  setForm((current) => ({
                    ...current,
                    schoolChoiceId: id,
                    school: school.name,
                    grade: "",
                  }));
                  antForm.setFieldValue("grade", undefined);
                }}
              />
            </Form.Item>
            <Form.Item
              name="grade"
              label="학년"
              rules={[{ required: true, message: "학년을 선택해 주세요." }]}
            >
              <Select
                disabled={locked || !selectedSchool}
                options={gradeOptions(selectedSchool?.schoolLevel)}
                placeholder="학년 선택"
                onChange={(grade) =>
                  setForm((current) => ({ ...current, grade }))}
              />
            </Form.Item>
          </div>
          <Typography.Paragraph type="secondary">
            목록에 없는 학교는 기존 학교 관리에서 추가한 뒤 선택해 주세요.
          </Typography.Paragraph>
          <Form.Item
            label="시험 기간"
            required
          >
            <DatePicker.RangePicker
              key={rangeKey}
              className="exam-input-range"
              value={[dayjs(form.inputStart), dayjs(form.inputEnd)]}
              format="YYYY-MM-DD"
              allowClear={false}
              disabled={locked}
              placeholder={["입력 시작일", "입력 종료일"]}
              onChange={changeRange}
            />
          </Form.Item>
        </section>
        <section className="exam-period-form-section">
          <div className="exam-period-form-section-heading">
            <h3>날짜별 시험 및 직보</h3>
            <span>여러 과목은 쉼표로 구분</span>
          </div>
          <Button
            block
            disabled={locked}
            className="exam-date-add"
            aria-label="앞 날짜 추가"
            onClick={() =>
              setForm((current) => extendExamForm(current, "first"))}
          >
            + 날짜 추가
          </Button>
          <Table
            className="exam-input-table"
            rowKey="date"
            pagination={false}
            size="small"
            scroll={{ x: 580 }}
            dataSource={dates.map((date) => ({ date }))}
            columns={[
              {
                title: "날짜",
                dataIndex: "date",
                width: 148,
                render: (date) => (
                  <span title={date}>
                    {dayjs(date).format("YYYY. M. D. (ddd)")}
                  </span>
                ),
              },
              {
                title: "시험 과목",
                key: "subject",
                render: (_, { date }) => (
                  <Input
                    disabled={locked}
                    value={examPlan(form, date).subject}
                    aria-label={date + " 시험 과목"}
                    placeholder="영어, 수학"
                    onChange={(event) =>
                      setPlan(date, "subject", event.target.value)}
                  />
                ),
              },
              {
                title: "직보 과목",
                key: "direct",
                render: (_, { date }) => (
                  <Input
                    disabled={locked}
                    value={examPlan(form, date).direct}
                    maxLength={200}
                    aria-label={date + " 직보 과목"}
                    placeholder="직보 과목"
                    onChange={(event) =>
                      setPlan(date, "direct", event.target.value)}
                  />
                ),
              },
              {
                title: "",
                key: "remove",
                width: 72,
                align: "center",
                render: (_, { date }) =>
                  dates.length > 1 &&
                    [form.inputStart, form.inputEnd].includes(date)
                    ? (
                      <Tooltip title="첫 날짜와 마지막 날짜만 삭제할 수 있습니다.">
                        <Button
                          className="exam-date-remove"
                          size="small"
                          type="text"
                          danger
                          disabled={locked}
                          aria-label={date + " 날짜 행 삭제"}
                          onClick={() => removeDate(date)}
                        >
                          ×
                        </Button>
                      </Tooltip>
                    )
                    : null,
              },
            ]}
          />
          <Button
            block
            disabled={locked}
            className="exam-date-add"
            aria-label="뒤 날짜 추가"
            onClick={() =>
              setForm((current) => extendExamForm(current, "last"))}
          >
            + 날짜 추가
          </Button>
        </section>
      </Form>
    </Drawer>
  );
}
